import React from 'react';
import { useTranslation } from 'react-i18next';
import { BrowseSwitcherLink, Link, useUrlBuilder } from '@app/router';
import { useExperiment } from '@app/experiments';
import { EXPERIMENTS, VERTICAL_URL_PARTS, PageType } from '@app/config';
import { ArrowRightIcon, TooltipIcon } from '@stockx/icons';
import {
  Button,
  Flex,
  Heading,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Text,
  usePopover,
  VisuallyHidden,
} from '@stockx/ui';
import { EVENTS, trackEvent } from '@app/segment';
import { useVertical } from '@app/catalog';

type ctaType = {
  title: string;
  url: string;
  urlType: string;
};

interface RowHeaderProps {
  header: string;
  seeAllCTA?: ctaType;
  seeAccountCTA?: ctaType;
  helpMessage: string;
  helpMessageTracking?: Function;
  rel?: string;
}

const DynamicRowHeader: React.FC<RowHeaderProps> = ({
  header,
  seeAllCTA,
  seeAccountCTA,
  helpMessage,
  helpMessageTracking,
  rel,
}) => {
  const { t } = useTranslation();
  const { sx } = usePopover();
  const url = seeAccountCTA?.url || seeAllCTA?.url || '';
  const buttonText = seeAccountCTA?.title || 'See All';
  const vertical =
    url.split('/').filter((piece) => VERTICAL_URL_PARTS.includes(piece))?.[0] ??
    '';

  const showUpdatedReleasesPage = useExperiment(
    EXPERIMENTS.feature_flag_releases_page_web,
  );

  const buildUrl = useUrlBuilder();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentVertical, setCurrentVertical] = useVertical();

  const getSeeAllCTA = () => {
    if (showUpdatedReleasesPage && seeAllCTA?.url.includes('/new-releases')) {
      return (
        <Link
          to={
            showUpdatedReleasesPage && seeAllCTA?.url.includes('/new-releases')
              ? buildUrl('/releases/upcoming')
              : url
          }
          rel={rel}
          updatedVertical={vertical.replace('-', ' ')}
          onClick={() => {
            if (helpMessageTracking) {
              helpMessageTracking();
            } else {
              trackEvent(EVENTS.SEE_ALL, {
                vertical: null,
                swooshName: header,
                screenName: PageType.Home,
              });
            }
          }}
        >
          <Button
            as="span"
            variant="link"
            tabIndex={-1}
            color="green.700"
            rightIcon={<ArrowRightIcon />}
          >
            {t(buttonText)}
          </Button>
          <VisuallyHidden>{header}</VisuallyHidden>
        </Link>
      );
    }

    return (
      <BrowseSwitcherLink
        to={
          showUpdatedReleasesPage && seeAllCTA?.url.includes('/new-releases')
            ? buildUrl('/releases/upcoming')
            : url
        }
        rel={rel}
        updatedVertical={vertical.replace('-', ' ')}
        onClick={() => {
          if (helpMessageTracking) {
            helpMessageTracking();
          } else {
            trackEvent(EVENTS.SEE_ALL, {
              vertical: null,
              swooshName: header,
              screenName: PageType.Home,
            });
          }
        }}
      >
        <Button
          as="span"
          variant="link"
          tabIndex={-1}
          color="green.700"
          rightIcon={<ArrowRightIcon />}
        >
          {t(buttonText)}
        </Button>
        <VisuallyHidden>{header}</VisuallyHidden>
      </BrowseSwitcherLink>
    );
  };

  return (
    <Flex mt="6" mb="4" justify="space-between" align="center">
      <Heading size="lg" variant="medium">
        {header}
        {helpMessage && (
          <Popover trigger={'hover'} placement="top">
            <PopoverTrigger>
              <Button
                p="1"
                mb="1"
                bg="transparent"
                _hover={{ bg: 'transparent' }}
                height="auto"
                aria-label={header}
              >
                <TooltipIcon
                  boxSize="5"
                  verticalAlign="-0.125em"
                  aria-hidden="true"
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent sx={sx.popoverContent}>
              <PopoverArrow />
              <PopoverBody>
                <Text fontSize="md">{helpMessage}</Text>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </Heading>
      {url?.length > 0 ? getSeeAllCTA() : null}
    </Flex>
  );
};

export default DynamicRowHeader;
